import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Stack,
  TextField,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import {
  ButtonAction,
  InformedIndividualCardWrapper,
  FormWrapper,
  GenericStyledBox,
  SelectWrapper,
  TypographyWrapper,
} from './styled-components';

import { blueGradientButton } from '../../../../../../../assets/styles/blueGradientButton.style';
import { useToastNotificationContext } from '../../../../../../../hooks/global/alert/useToastNotification';
import informedIndividualsService from '../../../../../../../services/informedIndividualsService';
import { RootState } from '../../../../../../../utils/store';

import getErrorMessage from '~/assets/utils/getErrorMessage';
import ErrorResponseObject from '~/interfaces/errorResponseObject';

interface InformIndividualDialogProps {
  open: boolean;
  handleClose: () => void;
  dialogTitle: string;
  content?: string;
  fetchInformedIndividuals: () => void;
}

type FieldWrapperProps = {
  children?: ReactNode;
  title: string;
  color?: string;
};

type FormFieldsType = {
  name: string;
  notificationReason: string;
  associatedDepartment: string;
  dateInformed: string;
};

const InformIndividualDialog = ({
  open,
  handleClose,
  dialogTitle,
  fetchInformedIndividuals,
}: InformIndividualDialogProps) => {
  const initialFields: FormFieldsType = {
    name: '',
    notificationReason: '',
    associatedDepartment: '',
    dateInformed: '',
  };

  const validateForm = (values: FormFieldsType) => {
    const errors = {
      name: '',
      notificationReason: '',
      associatedDepartment: '',
      dateInformed: '',
    };

    let hasErrors: boolean = false;
    if (values.name == '') {
      hasErrors = true;
      errors.name = 'Name is required.';
    }

    if (values.notificationReason == '') {
      hasErrors = true;
      errors.notificationReason = 'Notification Reason is required.';
    }

    if (!values.associatedDepartment) {
      hasErrors = true;
      errors.associatedDepartment = 'Associated Department is required.';
    }

    if (!values.dateInformed) {
      hasErrors = true;
      errors.dateInformed = 'Date Informed is required.';
    }

    return !hasErrors ? {} : errors;
  };

  const { createToastNotification } = useToastNotificationContext();

  const departments = useSelector(
    (state: RootState) => state.reports.departments,
  );
  const reportReference = useSelector(
    (state: RootState) => state.reports.currentReportReference,
  );

  const smallFieldStyles = {
    borderRadius: '6px',
    marginTop: '12px',
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(0, 0, 0,0.03)',
    },
    '& .MuiFilledInput-root.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0,0.03)',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
    opacity: 1,
  };

  const FieldWrapper = ({ children, title, color }: FieldWrapperProps) => (
    <Box marginBottom='24px' flexGrow={1}>
      <TypographyWrapper sx={{ color }}>{title}</TypographyWrapper>
      {children}
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
    >
      <InformedIndividualCardWrapper>
        <FormWrapper>
          <Formik
            initialValues={initialFields}
            validate={validateForm}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(async () => {
                try {
                  await informedIndividualsService.postInformedIndividual({
                    report: reportReference,
                    name: values.name,
                    associatedDepartment: values.associatedDepartment,
                    dateInformed: dayjs(values.dateInformed).format(
                      'YYYY-MM-DD',
                    ),
                    notificationReason: values.notificationReason,
                  });

                  createToastNotification(
                    'Informed Individual successfully created.',
                    'success',
                  );
                  setSubmitting(false);
                  fetchInformedIndividuals();

                  setTimeout(() => {
                    handleClose();
                  }, 3000);
                } catch (err) {
                  const customError = getErrorMessage(
                    err as ErrorResponseObject,
                  );
                  createToastNotification(customError, 'error');
                  setSubmitting(false);
                }
              }, 400);
            }}
          >
            {({
              values,
              isSubmitting,
              touched,
              setFieldValue,
              setFieldTouched,
              errors,
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack width='100%' direction='column'>
                  <Stack
                    width='100%'
                    sx={{ bgcolor: '#FFFFFF', p: '14px' }}
                    direction='row'
                    justifyContent='space-between'
                  >
                    <InformIndividualDialog.title>
                      {dialogTitle}
                    </InformIndividualDialog.title>
                    <Box
                      sx={{
                        width: '10%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Stack
                        direction='row'
                        sx={{
                          cursor: 'pointer !important',
                          alignItems: 'center',
                        }}
                      >
                        <ButtonAction
                          onClick={handleClose}
                          sx={{ color: '#878787' }}
                        >
                          {'Cancel'}
                        </ButtonAction>
                        <Button
                          type='submit'
                          disabled={isSubmitting}
                          sx={{
                            ...blueGradientButton,
                            width: '97px',
                            height: '38px',
                            padding: '14px 24px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundImage: 'transparent !important',
                            },
                            textTransform: 'none',
                          }}
                        >
                          {isSubmitting && <CircularProgress size={20} />}
                          {!isSubmitting && <>{'Add'}</>}
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack sx={{ p: '14px' }} width='100%' direction='row'>
                    <Stack direction='column' sx={{ width: '50%' }}>
                      <Stack direction='column'>
                        <FieldWrapper
                          color={
                            errors.name && touched.name ? 'red' : '#181818'
                          }
                          title='Full Name'
                        >
                          <TextField
                            fullWidth
                            name='name'
                            hiddenLabel
                            className={
                              errors.name && touched.name
                                ? 'borderErrorClass'
                                : ''
                            }
                            variant='filled'
                            sx={smallFieldStyles}
                            placeholder={'Enter full name'}
                            onChange={(e) => {
                              setFieldValue('name', e.target.value);
                            }}
                            onBlur={() => {
                              setFieldTouched('name', true);
                            }}
                            InputProps={{
                              style: {
                                height: '3.5rem',
                                overflow: 'hidden',
                                paddingLeft: '0.5rem',
                                paddingRight: '0.5rem',
                                border: '',
                                borderRadius: '6px',
                              },
                              disableUnderline: true,
                            }}
                            value={values.name}
                          />
                        </FieldWrapper>
                      </Stack>
                      <Stack>
                        <FieldWrapper
                          color={
                            errors.notificationReason &&
                            touched.notificationReason
                              ? 'red'
                              : '#181818'
                          }
                          title='Notification Reason'
                        >
                          <TextField
                            fullWidth
                            name='notificationReason'
                            hiddenLabel
                            className={
                              errors.notificationReason &&
                              touched.notificationReason
                                ? 'borderErrorClass'
                                : ''
                            }
                            variant='filled'
                            sx={smallFieldStyles}
                            placeholder={'Enter notification reason'}
                            onChange={(e) => {
                              const getValue = e.target.value;
                              setFieldValue('notificationReason', getValue);
                            }}
                            onBlur={() => {
                              setFieldTouched('notificationReason', true);
                            }}
                            InputProps={{
                              style: {
                                height: '3.5rem',
                                overflow: 'hidden',
                                paddingLeft: '0.5rem',
                                paddingRight: '0.5rem',
                                border: '',
                                borderRadius: '6px',
                              },
                              disableUnderline: true,
                            }}
                            value={values.notificationReason}
                          />
                        </FieldWrapper>
                      </Stack>
                    </Stack>
                    <Box sx={{ width: '3%' }}></Box>
                    <Stack sx={{ width: '50%' }} direction='column'>
                      <Stack direction='column'>
                        <FieldWrapper
                          color={
                            errors.associatedDepartment &&
                            touched.associatedDepartment
                              ? 'red'
                              : '#181818'
                          }
                          title='Associated Department'
                        >
                          <FormControl variant='standard' sx={{ width: '98%' }}>
                            <InputLabel
                              id='departmentAddId'
                              shrink={false}
                              disableAnimation={true}
                              sx={{
                                p: 0,
                                display:
                                  values.associatedDepartment != null &&
                                  values.associatedDepartment != ''
                                    ? 'none'
                                    : 'flex',
                                color: '#878787',
                                marginTop: '8px',
                                marginLeft: '12px',
                                '&.Mui-focused': {
                                  display: 'none !important',
                                },
                              }}
                            >
                              {'Select Department'}
                            </InputLabel>
                            <SelectWrapper
                              labelId='departmentAddId'
                              sx={{
                                paddingTop: '10px',
                                marginTop: '0px',
                                '& .MuiInput-input': {
                                  color: '#181818',
                                },
                                '& .MuiInput-input:focus': {
                                  backgroundColor: 'transparent !important',
                                },
                                '&.MuiInputBase-root': {
                                  marginTop: '12px !important',
                                },
                              }}
                              onChange={(e) => {
                                const getValue = e.target.value;
                                setFieldValue('associatedDepartment', getValue);
                              }}
                              IconComponent={() => (
                                <ExpandMoreIcon
                                  sx={{
                                    width: '30px',
                                    height: '30px',
                                  }}
                                />
                              )}
                              value={values.associatedDepartment}
                              label='Department'
                            >
                              <MenuItem value=''>
                                <em>{'Select Department'}</em>
                              </MenuItem>
                              {departments.map((department, key) => {
                                return (
                                  <MenuItem
                                    key={`mi-${key}`}
                                    value={department.value}
                                  >
                                    {department.label}
                                  </MenuItem>
                                );
                              })}
                            </SelectWrapper>
                          </FormControl>
                        </FieldWrapper>
                      </Stack>
                      <Stack direction='column'>
                        <FieldWrapper
                          color={
                            errors.dateInformed && touched.dateInformed
                              ? 'red'
                              : '#181818'
                          }
                          title='Date Informed'
                        >
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                              sx={{
                                mt: '12px',
                                width: '100%',
                                '& .MuiFormLabel-root': {
                                  color: '#878787',
                                },
                              }}
                              name='dateInformed'
                              label='Date Informed'
                              onChange={(value) => {
                                const getValue = value;
                                setFieldValue('dateInformed', getValue);
                              }}
                              value={
                                values.dateInformed == ''
                                  ? null
                                  : values.dateInformed
                              }
                              slotProps={{ textField: { variant: 'filled' } }}
                            />
                          </LocalizationProvider>
                        </FieldWrapper>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </form>
            )}
          </Formik>
        </FormWrapper>
      </InformedIndividualCardWrapper>
    </Modal>
  );
};

InformIndividualDialog.title = ({ children }: { children: string }) => {
  return (
    <GenericStyledBox
      sx={{
        width: '30%',
        alignItems: 'center',
        display: 'flex',
        color: '#181818',
        fontSize: '18px',
      }}
    >
      {children}
    </GenericStyledBox>
  );
};

export default InformIndividualDialog;
